import { useEffect, useRef, useState, type AnimationEvent } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  Gift,
  Globe,
  HelpCircle,
  LogIn,
  LogOut,
  Menu,
  Moon,
  Settings,
  Shield,
  ShoppingBag,
  Sun,
  Wallet,
  X,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { openWallet } from '../../navigation/openWallet';
import { TabFavoritesIcon, TabHomeIcon, TabPlansIcon } from './TabIcons';
import { useCurrentLanguage } from './LanguageSheet';

interface HamburgerMenuProps {
  className?: string;
  /** Opens the language bottom sheet — owned by the parent (MobileHomeTop) since it renders above everything, not just this drawer. */
  onOpenLanguage?: () => void;
}

const CLOSE_FALLBACK_MS = 300;

/**
 * The bottom tab bar already covers Home/Explore/Saved/My eSIMs/Profile, so
 * why duplicate them here too? Because a drawer is the classic "everything
 * else" surface — quick access without leaving the current tab, plus the
 * items that don't fit on a 5-tab bar at all (Top Up, Referral, Help,
 * Privacy). Language lives here instead of the navbar for the same reason
 * currency was removed from it: one glanceable row of icons up top, no
 * pills competing for attention.
 *
 * Full-height drawer sliding in from the left, mirroring the wallet sheet's
 * open/close animation pattern (TopupPage): `closing` plays the out-animation,
 * then `onAnimationEnd` (with a timeout fallback) actually unmounts it.
 */
export function HamburgerMenu({ className = '', onOpenLanguage }: HamburgerMenuProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, isAuthenticated, logout, unreadSupportChat } = useAuth();
  const { resolved, setMode } = useTheme();
  const currentLanguage = useCurrentLanguage();
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);

  const name = user?.first_name || profile?.firstName || (isAuthenticated ? t('common.user') : t('common.guest'));
  const initial = name.slice(0, 1).toUpperCase();

  const close = () => {
    if (!open || closing) return;
    setClosing(true);
  };

  useEffect(() => {
    if (!closing) return;
    const id = window.setTimeout(() => {
      setOpen(false);
      setClosing(false);
    }, CLOSE_FALLBACK_MS);
    return () => window.clearTimeout(id);
  }, [closing]);

  useEffect(() => {
    if (!open) return;

    const closeOnOutsideClick = (event: PointerEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (drawerRef.current?.contains(target)) return;
      close();
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('pointerdown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, closing]);

  const handleDrawerAnimationEnd = (event: AnimationEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (!closing) return;
    if (!event.animationName.includes('cs-hamburger-drawer-out')) return;
    setOpen(false);
    setClosing(false);
  };

  const go = (path: string) => {
    close();
    navigate(path);
  };

  const openTopUp = () => {
    close();
    if (isAuthenticated) {
      openWallet(navigate, location);
    } else {
      navigate('/login?returnTo=%2Ftopup');
    }
  };

  const openLanguage = () => {
    close();
    onOpenLanguage?.();
  };

  const handleLogout = () => {
    close();
    logout();
    navigate('/', { replace: true });
  };

  const navItems = [
    { key: 'home', label: t('nav.home'), Icon: TabHomeIcon, onClick: () => go('/') },
    { key: 'buy', label: t('nav.buyEsim'), Icon: ShoppingBag, onClick: () => go('/explore') },
    { key: 'my-esims', label: t('nav.myEsims'), Icon: TabPlansIcon, onClick: () => go('/my-esims') },
    { key: 'saved', label: t('nav.favorites'), Icon: TabFavoritesIcon, onClick: () => go('/favorites') },
    { key: 'topup', label: t('common.topUp'), Icon: Wallet, onClick: openTopUp },
  ];

  const utilityItems = [
    {
      key: 'language',
      label: t('settings.language'),
      Icon: Globe,
      onClick: openLanguage,
      trailing: `${currentLanguage.flag} ${currentLanguage.code.toUpperCase()}`,
    },
    { key: 'notifications', label: t('notifications.title'), Icon: Bell, onClick: () => go('/notifications') },
    { key: 'settings', label: t('settings.title'), Icon: Settings, onClick: () => go('/settings') },
  ];

  const supportItems = [
    { key: 'referral', label: t('profile.referralProgram'), Icon: Gift, onClick: () => go('/invite') },
    { key: 'help', label: t('nav.helpCenter'), Icon: HelpCircle, onClick: () => go('/support'), badge: unreadSupportChat },
    { key: 'privacy', label: t('nav.privacySecurity'), Icon: Shield, onClick: () => go('/settings/privacy') },
  ];

  return (
    <div ref={rootRef} className={className}>
      <button
        type="button"
        className="cs-m-top__icon-btn"
        aria-label={t('settings.title')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <Menu size={22} strokeWidth={2} />
      </button>

      {open &&
        createPortal(
          <>
            <div
              className={`cs-hamburger-drawer__backdrop${closing ? ' cs-hamburger-drawer__backdrop--closing' : ''}`}
              onClick={close}
              aria-hidden
            />
            <div
              ref={drawerRef}
              className={`cs-hamburger-drawer${closing ? ' cs-hamburger-drawer--closing' : ''}`}
              role="menu"
              aria-label={t('settings.title')}
              onAnimationEnd={handleDrawerAnimationEnd}
            >
              <div className="cs-hamburger-drawer__header">
                <button type="button" className="cs-hamburger-drawer__profile" onClick={() => go('/profile')}>
                  <span className="cs-hamburger-drawer__avatar" aria-hidden>
                    {profile?.avatarUrl ? <img src={profile.avatarUrl} alt="" /> : initial}
                  </span>
                  <span className="cs-hamburger-drawer__name">{name}</span>
                </button>
                <button type="button" className="cs-hamburger-drawer__close" aria-label={t('common.cancel')} onClick={close}>
                  <X size={18} strokeWidth={2.25} />
                </button>
              </div>

              <div className="cs-hamburger-drawer__items">
                {navItems.map((item) => (
                  <button key={item.key} type="button" role="menuitem" className="cs-hamburger-drawer__item" onClick={item.onClick}>
                    <span className="cs-hamburger-drawer__item-icon" aria-hidden>
                      <item.Icon size={19} strokeWidth={2} />
                    </span>
                    <span className="cs-hamburger-drawer__item-label">{item.label}</span>
                  </button>
                ))}
              </div>

              <div className="cs-hamburger-drawer__divider" aria-hidden />

              <div className="cs-hamburger-drawer__items">
                {utilityItems.map((item) => (
                  <button key={item.key} type="button" role="menuitem" className="cs-hamburger-drawer__item" onClick={item.onClick}>
                    <span className="cs-hamburger-drawer__item-icon" aria-hidden>
                      <item.Icon size={19} strokeWidth={2} />
                    </span>
                    <span className="cs-hamburger-drawer__item-label">{item.label}</span>
                    {'trailing' in item && item.trailing ? (
                      <span className="cs-hamburger-drawer__item-trailing">{item.trailing}</span>
                    ) : null}
                  </button>
                ))}
                <button
                  type="button"
                  role="menuitem"
                  className="cs-hamburger-drawer__item"
                  aria-label={t('home.toggleTheme')}
                  aria-pressed={resolved === 'dark'}
                  onClick={() => setMode(resolved === 'dark' ? 'light' : 'dark')}
                >
                  <span className="cs-hamburger-drawer__item-icon" aria-hidden>
                    {resolved === 'dark' ? <Moon size={19} strokeWidth={2} /> : <Sun size={19} strokeWidth={2} />}
                  </span>
                  <span className="cs-hamburger-drawer__item-label">{t('profile.theme')}</span>
                  <span className={`cs-toggle cs-hamburger-drawer__theme-toggle${resolved === 'dark' ? ' cs-toggle--on' : ''}`} aria-hidden>
                    <span className="cs-toggle__thumb" />
                  </span>
                </button>
              </div>

              <div className="cs-hamburger-drawer__divider" aria-hidden />

              <div className="cs-hamburger-drawer__items">
                {supportItems.map((item) => (
                  <button key={item.key} type="button" role="menuitem" className="cs-hamburger-drawer__item" onClick={item.onClick}>
                    <span className="cs-hamburger-drawer__item-icon" aria-hidden>
                      <item.Icon size={19} strokeWidth={2} />
                    </span>
                    <span className="cs-hamburger-drawer__item-label">{item.label}</span>
                    {item.badge ? (
                      <span className="cs-hamburger-menu__badge">{item.badge > 99 ? '99+' : item.badge}</span>
                    ) : null}
                  </button>
                ))}
              </div>

              <div className="cs-hamburger-drawer__footer">
                {isAuthenticated ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="cs-hamburger-drawer__item cs-hamburger-menu__danger"
                    onClick={handleLogout}
                  >
                    <span className="cs-hamburger-drawer__item-icon" aria-hidden>
                      <LogOut size={19} strokeWidth={2} />
                    </span>
                    <span className="cs-hamburger-drawer__item-label">{t('common.logout')}</span>
                  </button>
                ) : (
                  <button type="button" role="menuitem" className="cs-hamburger-drawer__item" onClick={() => go('/login')}>
                    <span className="cs-hamburger-drawer__item-icon" aria-hidden>
                      <LogIn size={19} strokeWidth={2} />
                    </span>
                    <span className="cs-hamburger-drawer__item-label">{t('auth.login')}</span>
                  </button>
                )}
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}
